export const SLR_LOAN_STANDING_FEATURE_FLAG = 'slrLoanStandingVerification';

export type SlrLoanStandingStatus =
  | 'unknown'
  | 'pending_upload'
  | 'processing'
  | 'eligible'
  | 'ineligible'
  | 'try_again'
  | 'cannot_determine';

export type SlrLoanStandingAction =
  'upload_document' | 'upload_newer_document' | 'confirm_name_match' | 'contact_support' | 'none';

/** Raw good-standing state from tio-api's EmployeePlanAccount `good_standing` column; `SlrLoanStandingStatus` is derived from this plus the reason via `deriveSlrLoanStandingStatus()`. */
export type SlrGoodStandingState = 'unknown' | 'eligible' | 'ineligible' | 'undeterminable';

/** Raw good-standing reason from tio-api's EmployeePlanAccount `good_standing_reason` column, plus the document-pipeline second-document asks that surface through the same field (tio-front-end#8714 / D4). */
export type SlrGoodStandingReason =
  | 'none'
  | 'missing_document'
  | 'stale_document'
  | 'outside_audit_window'
  | 'missing_required_observations'
  | 'name_mismatch'
  | 'name_match_confirmation_required'
  | 'status_not_in_good_standing'
  | 'delinquent_or_collections'
  | 'defaulted'
  | 'delinquent'
  | 'in_collections'
  | 'past_due'
  | 'parent_plus_only'
  | 'undeterminable'
  | 'manual_override'
  | 'nslds_required_for_federal_non_repayment_status'
  | 'statement_required_for_repayment_status'
  // Cable-only (Slr::VerificationOutcomeBroadcaster, #9972): the document reflected onto a different account. Never persisted on the EPA.
  | 'document_for_different_account';

export interface SlrLoanStandingFailure {
  code: string;
  field?: string;
  message?: string;
}

export interface SlrLoanStandingAuditState {
  employeePlanAccountId?: string | null;
  /** Name-review context for the fuzzy match (Figma v3 "Review Name" panel): the EF-sourced name vs. the borrower name extracted from the uploaded document. */
  employerProvidedName?: string | null;
  documentName?: string | null;
  status: SlrLoanStandingStatus;
  primaryReason?: string | null;
  reasons?: string[];
  failures?: SlrLoanStandingFailure[];
  action: SlrLoanStandingAction;
  sourceDocumentId?: string | null;
  /** Filename of the document behind the verdict — the name-match card's red callout (#10128). */
  sourceDocumentFilename?: string | null;
  featureEnabled: boolean;

  // Audit-window context (RFC 012): all three stay absent until tio-api exposes them, so shipping the UI ahead of the backend is safe.
  /** Upload deadline for the current audit window (ISO date) — drives the "required by" chip. */
  auditRequiredBy?: string | null;
  /** The audit deadline passed without a qualifying document — drives the "past due!" chip. */
  auditPastDue?: boolean;
  /** End of the contribution period the current audit covers (ISO date) — drives period-aware copy. */
  contributionPeriodEnd?: string | null;
  /** Verified accounts only — first day of the next audit month (ISO date), for the forward-looking reminder. */
  nextAuditStartsOn?: string | null;
}

/** Raw verdict broadcast by `Slr::VerificationOutcomeBroadcaster` on the asset's ServiceResponseChannel — deliberately raw; derive status/action via `deriveSlrLoanStandingStatus`/`deriveSlrLoanStandingAction`. */
export interface SlrLoanStandingCablePayload {
  employeePlanAccountId?: string | null;
  assetUuid?: string | null;
  sourceDocumentId?: string | null;
  goodStanding?: SlrGoodStandingState | string;
  goodStandingReason?: SlrGoodStandingReason | string | null;
  goodStandingReasons?: string[];
  goodStandingDeterminedAt?: string | null;
  // True when this verdict belongs to a different EPA than the one the document was submitted against (the account it actually reflected onto).
  redirected?: boolean;
  financialInstitutionName?: string | null;
}

export interface SlrLoanStandingPresentation {
  titleKey: string;
  descriptionKey: string;
  ctaKey?: string;
  intent: 'info' | 'warning' | 'success';
  requiresAttention: boolean;
}
